import type { ReactNode } from "react";
import { LangLink as Link } from "@/components/LangLink";
import { Reveal } from "@/components/Reveal";
import { useTranslation } from "react-i18next";

type ProductItem = {
  name: string;
  displayName?: ReactNode;
  desc: string;
  image?: string;
  link: string;
};

type ProductListProps = {
  products: ProductItem[];
};

export function ProductList({ products }: ProductListProps) {
  const { t } = useTranslation("common");
  return (
    <section className="container-bizu py-20 mb-20 space-y-px bg-border">
      <Reveal stagger>
        {products.map((p) => (
          <Link
            key={p.name}
            to={p.link}
            className="relative bg-background p-16 md:p-12 grid md:grid-cols-[1fr_2fr_60px] gap-6 items-center hover:bg-muted transition-colors group cursor-pointer overflow-visible"
          >
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent scale-y-0 origin-top transition-transform duration-300 group-hover:scale-y-100" />

            <h2 className="relative z-10 display text-2xl md:text-3xl">
              {p.displayName || p.name}
            </h2>

            <p className="relative z-10 text-muted-foreground leading-relaxed">{p.desc}</p>

            {p.image && (
              <div className="relative md:absolute md:inset-y-0 md:right-0 w-full md:w-[40%] h-auto md:h-full overflow-hidden mt-4 md:mt-0 opacity-100 md:opacity-0 transition-all duration-500 md:group-hover:opacity-100 z-0">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-auto md:h-full object-cover scale-100 md:scale-110 md:group-hover:scale-100 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-l from-transparent via-transparent to-background" />
              </div>
            )}

            <ArrowIcon className="hidden md:flex w-14 h-14 relative z-10" />
            <div className="md:hidden flex items-center gap-3 mt-6 text-accent font-semibold relative z-10">
              <span>{t("explore", "Explore")}</span>
              <ArrowIcon className="w-10 h-10" mobile />
            </div>
          </Link>
        ))}
      </Reveal>
    </section>
  );
}

function ArrowIcon({ className, mobile }: { className?: string; mobile?: boolean }) {
  return (
    <div
      className={`${className} rounded-full bg-accent items-center justify-center transition-transform duration-300 ${
        mobile ? "flex scale-100" : "scale-0 group-hover:scale-100 ml-auto flex-shrink-0"
      }`}
    >
      <svg
        className={mobile ? "w-5 h-5 text-black" : "w-6 h-6 text-black"}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.5}
          d="M13 7l5 5m0 0l-5 5m5-5H6"
        />
      </svg>
    </div>
  );
}
